"use client";

import clsx from "clsx";
import { Check, Loader2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import IconArrowDown from "@/components/Icons/IconArrowDown";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export interface ListingMobilePickerOption<T> {
  label: string;
  value: T;
}

interface ListingMobilePickerProps<T> {
  id: string;
  title: string;
  options: ListingMobilePickerOption<T>[];
  value: ListingMobilePickerOption<T> | null;
  isLoading?: boolean;
  noOptionsMessage?: string;
  onSelect: (option: ListingMobilePickerOption<T>) => void;
  onClear?: () => void;
}

const ListingMobilePicker = <T,>({
  id,
  title,
  options,
  value,
  isLoading = false,
  noOptionsMessage,
  onSelect,
  onClear,
}: ListingMobilePickerProps<T>) => {
  const t = useTranslations("Filters");
  const [isOpen, setIsOpen] = useState(false);
  const hasSelection = Boolean(value);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleSelect = (option: ListingMobilePickerOption<T>) => {
    onSelect(option);
    setIsOpen(false);
  };

  const handleClear = () => {
    onClear?.();
    setIsOpen(false);
  };

  return (
    <div className="h-full w-full">
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <button
            type="button"
            id={id}
            className={clsx(
              "inter relative flex h-full min-h-[3.5rem] w-full items-center justify-between gap-2 rounded-2xl border border-white bg-white px-3 py-3 font-semibold transition-all duration-300 hover:border-grey-border",
              isOpen && "border-grey-border ring-1 ring-grey-border",
            )}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={hasSelection ? `${title}, ${value?.label}` : title}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-primary-foreground">{title}</span>
              {hasSelection && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />}
            </span>
            {isLoading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
            ) : (
              <IconArrowDown
                className={clsx("shrink-0 transition-transform duration-300", isOpen && "rotate-180")}
                width="16"
                height="16"
              />
            )}
          </button>
        </SheetTrigger>

        <SheetContent
          side="bottom"
          hideCloseButton
          overlayClassName="z-[100] bg-black/50"
          className="z-[100] flex max-h-[85vh] flex-col gap-0 rounded-t-3xl border-0 bg-white p-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          aria-describedby={undefined}
        >
          <div className="flex justify-center pt-3" aria-hidden="true">
            <span className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>

          <SheetHeader className="flex-row items-center justify-between space-y-0 px-4 py-3 text-start">
            <SheetTitle className="urbanist text-xl font-semibold text-black">{title}</SheetTitle>
            <SheetClose asChild>
              <button type="button" className="rounded-full bg-[#345A8C1A] p-2 text-[#000000]" aria-label={t("close")}>
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetClose>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2" role="listbox" aria-label={title}>
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : options.length === 0 ? (
              <p className="inter px-4 py-8 text-center text-secondary-foreground">
                {noOptionsMessage || t("noOptionsAvailable")}
              </p>
            ) : (
              options.map((option) => {
                const isSelected = value?.label === option.label;

                return (
                  <button
                    key={option.label}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={clsx(
                      "inter flex w-full items-center justify-between rounded-2xl px-4 py-4 text-start text-base font-medium",
                      isSelected ? "bg-secondary-light text-black" : "text-gray-800 hover:bg-gray-50",
                    )}
                    onClick={() => handleSelect(option)}
                  >
                    <span>{option.label}</span>
                    {isSelected && <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />}
                  </button>
                );
              })
            )}
          </div>

          {onClear && hasSelection && (
            <div className="border-t p-4">
              <button
                type="button"
                onClick={handleClear}
                className="inter w-full rounded-xl border bg-primary-light px-4 py-3 text-center font-medium text-primary"
              >
                {t("clear")}
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ListingMobilePicker;
